import { useRef, useState } from 'react'
import { BellRing } from 'lucide-react'
import { PERMISSION_KEYS } from '@/lib/permissions'
import { usePermissionsAt } from '@/lib/use-permissions'
import {
  PolicyOrderGroup,
  policySelectorSummary,
  PRINTER_SELECTOR_FIELDS,
  type PolicyKindContribution,
  type PolicyListGroupProps,
} from '@/modules/hierarchy'
import { ConfirmDialog } from '@/ui/confirm-dialog'
import { Dialog, DialogContent } from '@/ui/dialog'
import {
  eventRulePoliciesApi,
  PRINTER_EVENT_RULE_POLICY_TYPE,
  type PrinterEventRulePolicy,
} from '../../api/event-rule-policies-api'
import { EventRulePolicyForm } from './event-rule-policy-form'
import { useDeleteEventRulePolicy } from './event-rule-policy-queries'
import { usePrinterPolicyGroup } from '../printer-policies/use-printer-policy-group'

export const printerEventRulePolicyKind: PolicyKindContribution = {
  valueType: PRINTER_EVENT_RULE_POLICY_TYPE,
  label: 'Event Rule',
  description:
    'Define an event condition and the actions to run when it matches.',
  icon: BellRing,
  order: 40,
  readPermission: PERMISSION_KEYS.alertRead,
  managePermission: PERMISSION_KEYS.alertManage,
  create: ({ entityId, onClose, onCreateLockChange }) => (
    <EventRulePolicyForm entityId={entityId} policy={null} onClose={onClose} onCreateLockChange={onCreateLockChange} />
  ),
  list: EventRulePolicyList,
}

function EventRulePolicyList({
  entityId,
  policies,
  complete,
  canReadHistory = false,
  returnFocus,
  grouped,
  onCreate,
  expansion,
  resolvedPolicy,
}: PolicyListGroupProps) {
  const { can } = usePermissionsAt(entityId)
  const canEdit = can(PERMISSION_KEYS.alertManage)
  const selectedId = expansion?.policyId
  const group = usePrinterPolicyGroup(entityId, PRINTER_EVENT_RULE_POLICY_TYPE, Boolean(grouped) && can(PERMISSION_KEYS.alertRead),
    policies.some((policy) => policy.id === selectedId) || resolvedPolicy?.id === selectedId ? selectedId : undefined)
  const listElement = useRef<HTMLElement | null>(null)
  const actionTrigger = useRef<HTMLButtonElement | null>(null)
  const remove = useDeleteEventRulePolicy(entityId)
  const [editing, setEditing] = useState<PrinterEventRulePolicy | null>(null)
  const [deleting, setDeleting] = useState<PrinterEventRulePolicy | null>(null)
  const [editorVersion, setEditorVersion] = useState(0)
  const [reloading, setReloading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  async function load(policyId: string, operation: 'edit' | 'delete') {
    if (!canEdit) return
    setReloading(true)
    setError(null)
    try {
      const detail = await eventRulePoliciesApi.get(entityId, policyId)
      if (operation === 'delete') {
        remove.reset()
        setDeleting(detail)
      } else {
        setEditing(detail)
        setEditorVersion((version) => version + 1)
      }
    } catch {
      setError('The event rule could not be loaded. Try again.')
    } finally {
      setReloading(false)
    }
  }
  function finalFocus() {
    if (actionTrigger.current?.isConnected) return actionTrigger.current
    return returnFocus?.() ?? listElement.current
  }

  return (
    <section ref={listElement} tabIndex={-1} className="flex flex-col gap-6" aria-label="Policies">
      {error && !editing && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
      <PolicyOrderGroup
        grouped={grouped}
        expansion={expansion}
        resolvedPolicy={resolvedPolicy}
        onCreate={onCreate}
        expanded={group.expanded}
        onExpandedChange={group.setExpanded}
        selectorSummary={(policy) => policySelectorSummary(policy.selectorExpression, policy.selectorGui, PRINTER_SELECTOR_FIELDS, 'All printers in scope')}
        entityId={entityId}
        valueType={PRINTER_EVENT_RULE_POLICY_TYPE}
        policies={policies}
        complete={complete}
        label="Printer event rules"
        composition="additive"
        canEdit={canEdit}
        canReadHistory={canReadHistory}
        reloading={reloading}
        onEdit={(policy, trigger) => { actionTrigger.current = trigger; void load(policy.id, 'edit') }}
        onDelete={(policy, trigger) => { actionTrigger.current = trigger; void load(policy.id, 'delete') }}
      />
      <Dialog
        open={editing !== null && canEdit}
        onOpenChange={(open) => {
          if (!open) setEditing(null)
        }}
      >
        <DialogContent finalFocus={finalFocus} className="max-h-[90dvh] max-w-3xl overflow-y-auto">
          {editing && canEdit && (
            <EventRulePolicyForm
              key={`${editing.policyId}:${editorVersion}`}
              entityId={entityId}
              policy={editing}
              onClose={() => setEditing(null)}
              onReload={() => void load(editing.policyId, 'edit')}
              reloading={reloading}
              reloadError={error}
            />
          )}
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        finalFocus={finalFocus}
        open={deleting !== null && canEdit}
        onOpenChange={(open) => {
          if (!open) setDeleting(null)
        }}
        title={`Delete ${deleting?.name ?? 'event rule'}?`}
        description="This rule will stop applying to printers. Other matching event rules remain in place."
        confirmLabel="Delete policy"
        pendingLabel="Deleting…"
        mutation={remove}
        errorMessage={() =>
          'The policy could not be deleted. Reload it if someone else changed it.'
        }
        onConfirm={() => {
          if (deleting)
            remove.mutate(deleting, {
              onSuccess: () => setDeleting(null),
            })
        }}
      />
    </section>
  )
}
